import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  PasswordInput,
  required,
  minLength,
  useGetList,
  useRedirect,
  useNotify,
  useEditController,
  Toolbar,
  SaveButton,
  SelectArrayInput,
} from 'react-admin';
import { Grid, Typography, Box } from '@mui/material';

export const UsersEdit: React.FC = () => {
  // 1) Controlador para tener record + loaded
  const { record } = useEditController();
  const redirect = useRedirect();
  const notify = useNotify();

  // 2) Carga roles disponibles
  const { data: roles = [], isLoading: rolesLoading } = useGetList('roles');
  // Mapeamos a choices que usan el name como valor
  const roleChoices = roles.map((r: any) => ({
    id: r.name, // valor interno
    name: r.name, // texto mostrado
  }));

  // Validación de contraseña
  const validatePassword = (value: string) =>
    value && value.trim() !== '' && value.length < 8
      ? 'La contraseña debe tener al menos 8 caracteres'
      : undefined;

  // Callbacks de la mutación
  const handleSuccess = () => {
    notify('Usuario actualizado exitosamente', { type: 'success' });
    redirect('show', 'users', record?.id);
  };
  const handleError = (error: any) => {
    notify(
      'Error al actualizar usuario: ' + (error.message || 'Error desconocido'),
      { type: 'error' }
    );
  };

  // Transform: quitamos solo la contraseña vacía
  const transform = (formData: any) => ({
    userName: formData.userName,
    email: formData.email,
    fullName: formData.fullName,
    phoneNumber: formData.phoneNumber || '',
    ...(formData.password?.trim() && { password: formData.password }),
    // Como backend acepta array de strings de nombres:
    roles: formData.roles || [],
  });

  // 3) Esperamos a que cargue el registro

  return (
    <Edit
      title='Editar Usuario'
      mutationMode='pessimistic'
      transform={transform}
      mutationOptions={{ onSuccess: handleSuccess, onError: handleError }}
    >
      <SimpleForm
        toolbar={
          <Toolbar>
            <SaveButton />
          </Toolbar>
        }
      >
        <Box sx={{ width: '100%', maxWidth: 600 }}>
          <Typography variant='h6' gutterBottom>
            Información Personal
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextInput
                source='userName'
                label='Nombre de Usuario'
                validate={[required(), minLength(3)]}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                source='email'
                label='Correo Electrónico'
                validate={[required()]}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source='fullName'
                label='Nombre Completo'
                validate={[required()]}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source='phoneNumber'
                label='Número de Teléfono'
                fullWidth
              />
            </Grid>
          </Grid>

          <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
            Cambiar Contraseña (Opcional)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <PasswordInput
                source='password'
                label='Nueva Contraseña'
                validate={validatePassword}
                helperText='Deja en blanco para mantener la contraseña actual'
                fullWidth
              />
            </Grid>
          </Grid>

          <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
            Roles
          </Typography>
          {rolesLoading ? (
            <Typography variant='body2' color='text.secondary'>
              Cargando roles…
            </Typography>
          ) : roleChoices.length === 0 ? (
            <Typography variant='body2' color='warning.main'>
              No hay roles disponibles, contacte al administrador.
            </Typography>
          ) : (
            <SelectArrayInput
              source='roles'
              choices={roleChoices}
              optionText='name'
              optionValue='id'
              helperText='Selecciona uno o más roles'
              fullWidth
            />
          )}
        </Box>
      </SimpleForm>
    </Edit>
  );
};
